import { Button, useTranslation } from '@packages/utils';
import { RoutesPaths } from 'routes/Routes.types';
import { useCheckPermission } from 'hooks/useCheckPermission';
import { useNavigate, useParams } from 'react-router-dom';
import { Lightning, ActionPlan } from 'assets/icon/icon';
import styled from 'styled-components';
import { useGetElcByIdQuery } from 'redux/apiSlice/elcs.slice';
import { IBreadcrumbItem } from 'components/Library/Breadcrumb/BreadcrumbExports';
import { useIsFeatureEnabled } from 'hooks/useIsFeatureEnabled';
import ELCDetail from '../ELCDetail';

const StyledButton = styled(Button)`
  && svg {
    transition: fill 0.2s ease, stroke 0.2s ease;
  }

  &&:hover svg path {
    fill: #3ba1b8;
    stroke: #3ba1b8;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const MyELCDetail = () => {
  const { t } = useTranslation({
    keyPrefix: 'descriptions.admin-portal.elcs.list.my-elcs',
  });
  const navigate = useNavigate();
  const params = useParams();
  const { id } = params;
  const { data } = useGetElcByIdQuery({
    elcId: id as string,
  });

  const isEditable = useCheckPermission('ELC_DETAILS_EDIT');
  const hasPillarsViewAccess = useCheckPermission('PILLARS_VIEW');
  const isPillarsManagementEnabled = useIsFeatureEnabled('pillars_management');

  const navigateToMyELCs = () => {
    navigate(`/app/${RoutesPaths.MYELCS}`);
  };

  const navigateToPillars = () => {
    if (!id) {
      console.error(
        'Cannot navigate to PillarsOfStrength: Missing ID parameter',
      );
      return;
    }

    const routePath = RoutesPaths.MYELCS_PILLARS_OF_STRENGTH;
    const pillarsPath = `/app/${routePath}`
      .replace(':id', id)
      .replace(':pillarId', id);

    navigate(pillarsPath);
  };

  const handleActionPlanClick = () => {
    if (!id) {
      console.error('Cannot navigate to Action Plan: Missing ID parameter');
      return;
    }
    const actionPlanPath = `/app/${RoutesPaths.MYELCS_ACTION_PLAN.replace(
      ':id',
      id,
    )}`;
    navigate(actionPlanPath);
  };

  const pillarsButton = hasPillarsViewAccess ? (
    <StyledButton
      key="pillars-button"
      onClick={navigateToPillars}
      color="primary"
      icon={<Lightning size={14} />}
      data-cy="pillars_btn"
    >
      Pillars
    </StyledButton>
  ) : undefined;

  const actionPlanButton = isPillarsManagementEnabled ? (
    <StyledButton
      key="action-plan-button"
      onClick={handleActionPlanClick}
      color="primary"
      icon={<ActionPlan size={14} />}
      data-cy="action_plan_btn"
    >
      Action Plan
    </StyledButton>
  ) : undefined;

  const extraActions = (
    <ButtonGroup>
      {pillarsButton}
      {actionPlanButton}
    </ButtonGroup>
  );

  const breadcrumbItems: IBreadcrumbItem[] = [
    {
      label: t('TITLE'),
      onClick: navigateToMyELCs,
    },
    {
      label: data?.name || '',
    },
  ];

  return (
    <ELCDetail
      isDetailsEditable={isEditable}
      backUrl={`/app/${RoutesPaths.MYELCS}`}
      backButtonSuffix={t('TITLE')}
      extraActions={extraActions}
      breadcrumbItems={breadcrumbItems}
    />
  );
};

export default MyELCDetail;
